import { useEffect, useState } from 'react';

import { PAX_TYPE } from 'app/typings/core/admin/enum/paxType';
import { OPENING_CONFIGURATION_TYPE } from 'app/typings/core/admin/experiences/enum/openingConfiguration';
import type { DayOfWeek } from 'app/typings/core/common/enum/dayOfWeek';
import { DAY_OF_WEEK } from 'app/typings/core/common/enum/dayOfWeek';
import { PRICE_DIFFERENTIATION_MODE } from 'app/typings/core/common/openingConfiguration/priceConfigurationType';
import type {
  BasePriceConfiguration,
  PriceConfigurationType,
} from 'app/typings/core/common/openingConfiguration/priceConfigurationType';
import type { StockConfigurationType } from 'app/typings/core/common/openingConfiguration/stockConfigurationType';
import { ExperienceType } from 'app/typings/experiences';

import type { ExperienceInitialFormType } from './useExperienceData';

export type ValidationError = {
  message: string;
  type: string;
  days?: DayOfWeek[];
};

export type StockValidationError = ValidationError & {
  isClosed: boolean;
  stock?: number | null;
};

export type PriceValidationError = ValidationError & {
  price: number;
  missingFields?: string[];
  fieldsByDay?: Record<DayOfWeek, string[]>;
};

export type ValidationErrorResult<T extends ValidationError = ValidationError> =
  {
    isValid: boolean;
    errors: T[];
  };

export type FormValidationErrors = {
  stockError: ValidationErrorResult;
  priceError: ValidationErrorResult;
};

type Subscriber = (errors: FormValidationErrors) => void;

const subscribers = new Set<Subscriber>();

const createEmptyErrors = (): FormValidationErrors => ({
  stockError: { isValid: true, errors: [] },
  priceError: { isValid: true, errors: [] },
});

const notifySubscribers = (): void => {
  subscribers.forEach((subscriber) => subscriber(validationStore.errors));
};

const validationStore = {
  errors: createEmptyErrors(),
};

export const setValidationErrors = (errors: FormValidationErrors): void => {
  validationStore.errors = errors;
  notifySubscribers();
};

export const setStockValidationError = (error: ValidationErrorResult): void => {
  validationStore.errors.stockError = error;
  notifySubscribers();
};

export const setPriceValidationError = (error: ValidationErrorResult): void => {
  validationStore.errors.priceError = error;
  notifySubscribers();
};

export const useValidationErrors = (): FormValidationErrors => {
  const [errors, setErrors] = useState<FormValidationErrors>(
    validationStore.errors
  );

  useEffect(() => {
    subscribers.add(setErrors);

    return () => {
      subscribers.delete(setErrors);
    };
  }, []);

  return errors;
};

export const resetValidationErrors = (): void => {
  validationStore.errors = createEmptyErrors();
  notifySubscribers();
};

// ============================================================================
// Form dirty state (for programmatic form changes that bypass onFieldsChange)
// ============================================================================

let formDirtyFlag = false;
let onFormDirtyCallback: (() => void) | null = null;

export const registerFormDirtyCallback = (callback: () => void): void => {
  onFormDirtyCallback = callback;
};

export const unregisterFormDirtyCallback = (): void => {
  onFormDirtyCallback = null;
};

export const markFormDirty = (): void => {
  formDirtyFlag = true;

  if (onFormDirtyCallback) {
    onFormDirtyCallback();
  }
};

export const isFormDirty = (): boolean => formDirtyFlag;

export const resetFormDirty = (): void => {
  formDirtyFlag = false;
};

// ============================================================================
// Helper functions
// ============================================================================

type StockConfigForDay = {
  stock?: number | null;
  isClosed: boolean;
};

const getStockConfigurationForDay = (
  stockConfiguration: StockConfigurationType,
  dayOfWeek: DayOfWeek
): StockConfigForDay => {
  if (stockConfiguration.type === OPENING_CONFIGURATION_TYPE.DAILY) {
    return (
      stockConfiguration.days[dayOfWeek] ?? {
        stock: undefined,
        isClosed: false,
      }
    );
  }

  if (stockConfiguration.configuration !== undefined) {
    return {
      stock: stockConfiguration.configuration.stock,
      isClosed: false,
    };
  }

  return {
    stock: undefined,
    isClosed: false,
  };
};

const getPriceConfigurationForDay = (
  priceConfiguration: PriceConfigurationType,
  dayOfWeek: DayOfWeek
): BasePriceConfiguration | undefined => {
  if (priceConfiguration.type === OPENING_CONFIGURATION_TYPE.DAILY) {
    return priceConfiguration.days?.[dayOfWeek];
  }

  return priceConfiguration.configuration;
};

// ============================================================================
// Validation functions - Return ValidationErrorResult
// ============================================================================

const validateClosedStockConfiguration = (
  stockConfig: StockConfigForDay
): string | null => {
  if (stockConfig.isClosed && stockConfig.stock && stockConfig.stock > 0) {
    return 'Stock is closed and stock is greater than 0';
  }

  return null;
};

const validateStaycationExperience = (
  stockConfiguration: StockConfigurationType,
  priceConfiguration: PriceConfigurationType,
  stockForDay: StockConfigForDay,
  priceForDay: BasePriceConfiguration | undefined
): string | null => {
  if (stockConfiguration.type !== OPENING_CONFIGURATION_TYPE.CONSTANT) {
    return 'Activity originals must be constant';
  }

  if (priceConfiguration.type !== OPENING_CONFIGURATION_TYPE.CONSTANT) {
    return 'Activity originals must be constant';
  }

  if (
    priceConfiguration.priceDifferentiationMode !==
    PRICE_DIFFERENTIATION_MODE.NONE
  ) {
    return 'Price differentiation mode is not NONE for originals activity';
  }

  if (stockForDay.stock !== undefined && stockForDay.stock !== null) {
    return 'Stock is not infinite for staycation activity';
  }

  if (
    isValueEmpty(priceForDay?.price) ||
    isValueEmpty(priceForDay?.negotiatedPrice) ||
    isValueEmpty(priceForDay?.staycationPrice) ||
    !isValueEmpty(priceForDay?.daytimePrice) ||
    !isValueEmpty(priceForDay?.childPrice) ||
    !isValueEmpty(priceForDay?.childDaytimePrice)
  ) {
    return 'Price configuration is not correct for originals';
  }

  return null;
};

const validateClosedOpeningConfiguration = (
  priceConfiguration: PriceConfigurationType,
  stockForDay: StockConfigForDay,
  priceForDay: BasePriceConfiguration | undefined,
  isAlwaysIncludedWithNight: boolean,
  preservePriceForClosedDay = true
): string | null => {
  if (isAlwaysIncludedWithNight) {
    return 'Closed opening configuration is not allowed for always included with night experience';
  }

  if (stockForDay.stock !== 0) {
    return 'Stock is not 0 for closed opening configuration';
  }

  if (!preservePriceForClosedDay) {
    if (
      priceConfiguration.type === OPENING_CONFIGURATION_TYPE.DAILY &&
      (!isValueEmpty(priceForDay?.price) ||
        !isValueEmpty(priceForDay?.daytimePrice) ||
        !isValueEmpty(priceForDay?.childPrice) ||
        !isValueEmpty(priceForDay?.childDaytimePrice) ||
        !isValueEmpty(priceForDay?.negotiatedPrice) ||
        !isValueEmpty(priceForDay?.staycationPrice))
    ) {
      return 'Price configuration is not empty for closed opening configuration';
    }
  }

  return null;
};

const validateAlwaysIncludedWithNightConfiguration = (
  priceConfiguration: PriceConfigurationType,
  priceForDay: BasePriceConfiguration | undefined,
  allowedPaxTypes?: string[]
): string | null => {
  const hasAdultOnly =
    allowedPaxTypes?.length === 1 && allowedPaxTypes[0] === PAX_TYPE.ADULT;
  const hasChildOnly =
    allowedPaxTypes?.length === 1 && allowedPaxTypes[0] === PAX_TYPE.CHILD;

  const hasBothPaxTypes = !hasAdultOnly && !hasChildOnly;

  if (
    priceConfiguration.priceDifferentiationMode !==
      PRICE_DIFFERENTIATION_MODE.WITH_DAYTIME &&
    priceConfiguration.priceDifferentiationMode !==
      PRICE_DIFFERENTIATION_MODE.WITH_CHILD_DAYTIME
  ) {
    return 'Price must be configured as daytime or child daytime price for always included with night experience';
  }

  if (
    priceConfiguration.priceDifferentiationMode ===
    PRICE_DIFFERENTIATION_MODE.WITH_DAYTIME
  ) {
    if (hasChildOnly) {
      if (
        isValueEmpty(priceForDay?.childDaytimePrice) ||
        !isValueEmpty(priceForDay?.price) ||
        !isValueEmpty(priceForDay?.daytimePrice) ||
        !isValueEmpty(priceForDay?.childPrice)
      ) {
        return 'Only child daytime price should be defined for child only guests';
      }
    } else if (hasAdultOnly) {
      if (
        !isValueEmpty(priceForDay?.price) ||
        isValueEmpty(priceForDay?.daytimePrice) ||
        !isValueEmpty(priceForDay?.childPrice) ||
        !isValueEmpty(priceForDay?.childDaytimePrice)
      ) {
        return 'Only daytime price should be defined for adult only guests';
      }
    } else if (hasBothPaxTypes) {
      if (
        !isValueEmpty(priceForDay?.price) ||
        isValueEmpty(priceForDay?.daytimePrice) ||
        !isValueEmpty(priceForDay?.childPrice) ||
        !isValueEmpty(priceForDay?.childDaytimePrice)
      ) {
        return 'Only daytime price should be defined for adult only guests';
      }
    } else {
      return 'This case should not happen, contact a developer';
    }
  }

  if (
    priceConfiguration.priceDifferentiationMode ===
    PRICE_DIFFERENTIATION_MODE.WITH_CHILD_DAYTIME
  ) {
    if (hasAdultOnly || hasChildOnly) {
      return 'WITH_CHILD_DAYTIME mode requires both adult and child guests';
    }

    if (
      !isValueEmpty(priceForDay?.price) ||
      isValueEmpty(priceForDay?.daytimePrice) ||
      !isValueEmpty(priceForDay?.childPrice) ||
      isValueEmpty(priceForDay?.childDaytimePrice)
    ) {
      return 'Daytime price and child daytime price must be defined, night prices should not be set';
    }
  }

  return null;
};

const validatePriceDifferentiationMode = (
  priceForDay: BasePriceConfiguration | undefined,
  priceDifferentiationMode: string,
  allowedPaxTypes?: string[],
  isClosed = false
): string | null => {
  if (isClosed) {
    return null;
  }

  const hasAdultOnly =
    allowedPaxTypes?.length === 1 && allowedPaxTypes[0] === PAX_TYPE.ADULT;
  const hasChildOnly =
    allowedPaxTypes?.length === 1 && allowedPaxTypes[0] === PAX_TYPE.CHILD;

  switch (priceDifferentiationMode) {
    case PRICE_DIFFERENTIATION_MODE.NONE:
      if (hasChildOnly) {
        if (isValueEmpty(priceForDay?.childPrice)) {
          return 'Child price is required';
        }

        if (!isValueEmpty(priceForDay?.price)) {
          return 'Price should not be set for child only guests';
        }
      } else {
        if (isValueEmpty(priceForDay?.price)) {
          return 'Price is required';
        }

        if (hasAdultOnly && !isValueEmpty(priceForDay?.childPrice)) {
          return 'Child price should not be set for adult only guests';
        }
      }

      break;

    case PRICE_DIFFERENTIATION_MODE.WITH_CHILD:
      if (isValueEmpty(priceForDay?.price)) {
        return 'Price is required';
      }

      if (isValueEmpty(priceForDay?.childPrice)) {
        return 'Child price is required';
      }

      break;

    case PRICE_DIFFERENTIATION_MODE.WITH_CHILD_DAYTIME:
      if (isValueEmpty(priceForDay?.price)) {
        return 'Price is required';
      }

      if (isValueEmpty(priceForDay?.childPrice)) {
        return 'Child price is required';
      }

      if (isValueEmpty(priceForDay?.daytimePrice)) {
        return 'Daytime price is required';
      }

      if (isValueEmpty(priceForDay?.childDaytimePrice)) {
        return 'Child daytime price is required';
      }

      break;

    case PRICE_DIFFERENTIATION_MODE.WITH_DAYTIME:
      if (hasChildOnly) {
        if (isValueEmpty(priceForDay?.childDaytimePrice)) {
          return 'Child daytime price is required';
        }

        if (!isValueEmpty(priceForDay?.daytimePrice)) {
          return 'Daytime price should not be set for child only guests';
        }
      } else {
        if (isValueEmpty(priceForDay?.price)) {
          return 'Price is required';
        }

        if (isValueEmpty(priceForDay?.daytimePrice)) {
          return 'Daytime price is required';
        }

        if (hasAdultOnly && !isValueEmpty(priceForDay?.childDaytimePrice)) {
          return 'Child daytime price should not be set for adult only guests';
        }
      }

      break;
  }

  return null;
};

const getMissingPriceFields = (
  priceForDay: BasePriceConfiguration | undefined,
  priceDifferentiationMode: string,
  isAlwaysIncludedWithNight: boolean,
  allowedPaxTypes?: string[]
): string[] => {
  const missingFields: string[] = [];

  const hasChildOnly =
    allowedPaxTypes?.length === 1 && allowedPaxTypes[0] === PAX_TYPE.CHILD;

  switch (priceDifferentiationMode) {
    case PRICE_DIFFERENTIATION_MODE.NONE:
      if (hasChildOnly) {
        if (isValueEmpty(priceForDay?.childPrice)) {
          missingFields.push('childPrice');
        }
      } else {
        if (isValueEmpty(priceForDay?.price)) {
          missingFields.push('price');
        }
      }

      break;

    case PRICE_DIFFERENTIATION_MODE.WITH_CHILD:
      if (isValueEmpty(priceForDay?.price)) {
        missingFields.push('price');
      }

      if (isValueEmpty(priceForDay?.childPrice)) {
        missingFields.push('childPrice');
      }

      break;

    case PRICE_DIFFERENTIATION_MODE.WITH_CHILD_DAYTIME:
      if (isAlwaysIncludedWithNight) {
        if (isValueEmpty(priceForDay?.daytimePrice)) {
          missingFields.push('daytimePrice');
        }

        if (isValueEmpty(priceForDay?.childDaytimePrice)) {
          missingFields.push('childDaytimePrice');
        }
      } else {
        if (isValueEmpty(priceForDay?.price)) {
          missingFields.push('price');
        }

        if (isValueEmpty(priceForDay?.childPrice)) {
          missingFields.push('childPrice');
        }

        if (isValueEmpty(priceForDay?.daytimePrice)) {
          missingFields.push('daytimePrice');
        }

        if (isValueEmpty(priceForDay?.childDaytimePrice)) {
          missingFields.push('childDaytimePrice');
        }
      }

      break;

    case PRICE_DIFFERENTIATION_MODE.WITH_DAYTIME:
      if (isAlwaysIncludedWithNight) {
        if (hasChildOnly) {
          if (isValueEmpty(priceForDay?.childDaytimePrice)) {
            missingFields.push('childDaytimePrice');
          }
        } else {
          if (isValueEmpty(priceForDay?.daytimePrice)) {
            missingFields.push('daytimePrice');
          }
        }
      } else {
        if (hasChildOnly) {
          if (isValueEmpty(priceForDay?.childDaytimePrice)) {
            missingFields.push('childDaytimePrice');
          }
        } else {
          if (isValueEmpty(priceForDay?.price)) {
            missingFields.push('price');
          }

          if (isValueEmpty(priceForDay?.daytimePrice)) {
            missingFields.push('daytimePrice');
          }
        }
      }

      break;
  }

  return missingFields;
};

const isValueEmpty = (value: number | undefined | null): boolean => {
  return value === undefined || value === null;
};

const addMissingFieldsToError = (
  errorData: {
    days: DayOfWeek[];
    price?: number;
    type?: string;
    message?: string;
    fieldsByDay?: Record<DayOfWeek, string[]>;
  },
  day: DayOfWeek,
  missingFields: string[]
): void => {
  if (missingFields.length > 0) {
    if (!errorData.fieldsByDay) {
      errorData.fieldsByDay = {} as Record<DayOfWeek, string[]>;
    }

    errorData.fieldsByDay[day] = missingFields;
  }
};

const getMissingAlwaysIncludedWithNightFields = (
  priceConfiguration: PriceConfigurationType,
  priceForDay: BasePriceConfiguration | undefined,
  allowedPaxTypes?: string[]
): string[] => {
  const missingFields: string[] = [];

  const hasChildOnly =
    allowedPaxTypes?.length === 1 && allowedPaxTypes[0] === PAX_TYPE.CHILD;

  if (
    priceConfiguration.priceDifferentiationMode ===
    PRICE_DIFFERENTIATION_MODE.WITH_DAYTIME
  ) {
    if (hasChildOnly) {
      if (isValueEmpty(priceForDay?.childDaytimePrice)) {
        missingFields.push('childDaytimePrice');
      }

      if (!isValueEmpty(priceForDay?.price)) {
        missingFields.push('price');
      }

      if (!isValueEmpty(priceForDay?.daytimePrice)) {
        missingFields.push('daytimePrice');
      }

      if (!isValueEmpty(priceForDay?.childPrice)) {
        missingFields.push('childPrice');
      }
    } else {
      if (isValueEmpty(priceForDay?.daytimePrice)) {
        missingFields.push('daytimePrice');
      }

      if (!isValueEmpty(priceForDay?.price)) {
        missingFields.push('price');
      }

      if (!isValueEmpty(priceForDay?.childPrice)) {
        missingFields.push('childPrice');
      }

      if (!isValueEmpty(priceForDay?.childDaytimePrice)) {
        missingFields.push('childDaytimePrice');
      }
    }
  }

  if (
    priceConfiguration.priceDifferentiationMode ===
    PRICE_DIFFERENTIATION_MODE.WITH_CHILD_DAYTIME
  ) {
    if (isValueEmpty(priceForDay?.daytimePrice)) {
      missingFields.push('daytimePrice');
    }

    if (isValueEmpty(priceForDay?.childDaytimePrice)) {
      missingFields.push('childDaytimePrice');
    }

    if (!isValueEmpty(priceForDay?.price)) {
      missingFields.push('price');
    }

    if (!isValueEmpty(priceForDay?.childPrice)) {
      missingFields.push('childPrice');
    }
  }

  return missingFields;
};

// ============================================================================
// Main validation functions
// ============================================================================

const validateStock = (
  formValues: ExperienceInitialFormType
): ValidationErrorResult => {
  const errors: StockValidationError[] = [];
  const stockConfig = formValues?.openingConfiguration?.stockConfiguration;

  if (!stockConfig) {
    return { isValid: true, errors };
  }

  if (stockConfig.type === OPENING_CONFIGURATION_TYPE.CONSTANT) {
    if (
      stockConfig.configuration &&
      stockConfig.configuration.stock !== undefined &&
      stockConfig.configuration.stock &&
      stockConfig.configuration.stock < 0
    ) {
      errors.push({
        type: OPENING_CONFIGURATION_TYPE.CONSTANT,
        isClosed: false,
        stock: stockConfig.configuration.stock,
        message: 'Stock cannot be negative',
      });
    }

    return { isValid: errors.length === 0, errors };
  }

  if (stockConfig.type === OPENING_CONFIGURATION_TYPE.DAILY) {
    const days = stockConfig.days;

    if (!days || typeof days !== 'object') {
      return { isValid: true, errors };
    }

    const errorsByMessage: Record<
      string,
      { days: DayOfWeek[]; isClosed: boolean; stock: number }
    > = {};

    Object.entries(days).forEach(
      ([day, dayConfig]: [
        string,
        { isClosed: boolean; stock?: number | null }
      ]) => {
        if (!dayConfig) {
          return;
        }

        const error = validateClosedStockConfiguration(dayConfig);

        if (error) {
          if (!errorsByMessage[error]) {
            errorsByMessage[error] = {
              days: [],
              isClosed: dayConfig.isClosed ?? false,
              stock: dayConfig.stock ?? 0,
            };
          }

          errorsByMessage[error].days.push(day as DayOfWeek);
        }
      }
    );

    Object.entries(errorsByMessage).forEach(([message, data]) => {
      errors.push({
        days: data.days,
        isClosed: data.isClosed,
        stock: data.stock,
        type: OPENING_CONFIGURATION_TYPE.DAILY,
        message,
      });
    });
  }

  return { isValid: errors.length === 0, errors };
};

const validatePrice = (
  formValues: ExperienceInitialFormType
): ValidationErrorResult => {
  const errors: PriceValidationError[] = [];
  const priceConfig = formValues?.openingConfiguration?.priceConfiguration;
  const stockConfig = formValues?.openingConfiguration?.stockConfiguration;
  const allowedPaxTypes = formValues?.allowedPaxTypes;
  const isAlwaysIncludedWithNight = formValues.isAlwaysIncludedWithNight;

  if (!priceConfig) {
    return { isValid: true, errors };
  }

  if (priceConfig.type === OPENING_CONFIGURATION_TYPE.CONSTANT) {
    const missingFields = getMissingPriceFields(
      priceConfig.configuration,
      priceConfig.priceDifferentiationMode,
      isAlwaysIncludedWithNight,
      allowedPaxTypes
    );

    if (missingFields.length > 0) {
      errors.push({
        price: 0,
        type: OPENING_CONFIGURATION_TYPE.CONSTANT,
        message: 'Price configuration is incomplete',
        missingFields,
      });
    }

    return { isValid: errors.length === 0, errors };
  }

  if (
    priceConfig.type === OPENING_CONFIGURATION_TYPE.DAILY &&
    stockConfig?.type === OPENING_CONFIGURATION_TYPE.DAILY
  ) {
    const stockDays = stockConfig.days;

    if (stockDays) {
      type ErrorData = {
        days: DayOfWeek[];
        price: number;
        fieldsByDay?: Record<DayOfWeek, string[]>;
      };

      const errorsByMessage: Record<string, ErrorData> = {};

      Object.entries(stockDays).forEach(
        ([day, dayStockConfig]: [
          string,
          { isClosed?: boolean; stock?: number | null }
        ]) => {
          if (!dayStockConfig) {
            return;
          }

          const isClosed = dayStockConfig.isClosed ?? false;

          if (isClosed) {
            return;
          }

          const dayPriceConfig = getPriceConfigurationForDay(
            priceConfig,
            day as DayOfWeek
          );

          const error = isAlwaysIncludedWithNight
            ? validateAlwaysIncludedWithNightConfiguration(
                priceConfig,
                dayPriceConfig,
                allowedPaxTypes
              )
            : validatePriceDifferentiationMode(
                dayPriceConfig,
                priceConfig.priceDifferentiationMode,
                allowedPaxTypes
              );

          if (error) {
            if (!errorsByMessage[error]) {
              errorsByMessage[error] = { days: [], price: 0 };
            }

            errorsByMessage[error].days.push(day as DayOfWeek);

            const missingFields = isAlwaysIncludedWithNight
              ? getMissingAlwaysIncludedWithNightFields(
                  priceConfig,
                  dayPriceConfig,
                  allowedPaxTypes
                )
              : getMissingPriceFields(
                  dayPriceConfig,
                  priceConfig.priceDifferentiationMode,
                  isAlwaysIncludedWithNight,
                  allowedPaxTypes
                );

            addMissingFieldsToError(
              errorsByMessage[error],
              day as DayOfWeek,
              missingFields
            );
          }
        }
      );

      Object.entries(errorsByMessage).forEach(([message, data]) => {
        errors.push({
          days: data.days,
          price: data.price,
          type: OPENING_CONFIGURATION_TYPE.DAILY,
          message,
          fieldsByDay: data.fieldsByDay,
        });
      });
    }
  } else if (
    priceConfig.type === OPENING_CONFIGURATION_TYPE.DAILY &&
    stockConfig?.type === OPENING_CONFIGURATION_TYPE.CONSTANT
  ) {
    const priceDays = priceConfig.days || {};

    type ErrorData = {
      days: DayOfWeek[];
      price: number;
      fieldsByDay?: Record<DayOfWeek, string[]>;
    };

    const errorsByMessage: Record<string, ErrorData> = {};

    Object.entries(priceDays).forEach(
      ([day, dayPriceConfig]: [string, BasePriceConfiguration | undefined]) => {
        if (!dayPriceConfig) {
          return;
        }

        let error: string | null = null;

        if (isAlwaysIncludedWithNight) {
          error = validateAlwaysIncludedWithNightConfiguration(
            priceConfig,
            dayPriceConfig,
            allowedPaxTypes
          );
        } else {
          error = validatePriceDifferentiationMode(
            dayPriceConfig,
            priceConfig.priceDifferentiationMode,
            allowedPaxTypes
          );
        }

        if (error) {
          if (!errorsByMessage[error]) {
            errorsByMessage[error] = { days: [], price: 0 };
          }

          errorsByMessage[error].days.push(day as DayOfWeek);

          const missingFields = isAlwaysIncludedWithNight
            ? getMissingAlwaysIncludedWithNightFields(
                priceConfig,
                dayPriceConfig,
                allowedPaxTypes
              )
            : getMissingPriceFields(
                dayPriceConfig,
                priceConfig.priceDifferentiationMode,
                isAlwaysIncludedWithNight,
                allowedPaxTypes
              );

          addMissingFieldsToError(
            errorsByMessage[error],
            day as DayOfWeek,
            missingFields
          );
        }
      }
    );

    Object.entries(errorsByMessage).forEach(([message, data]) => {
      errors.push({
        days: data.days,
        price: data.price,
        type: OPENING_CONFIGURATION_TYPE.DAILY,
        message,
        fieldsByDay: data.fieldsByDay,
      });
    });
  }

  return { isValid: errors.length === 0, errors };
};

export const validateExperienceConfiguration = (
  formValues: ExperienceInitialFormType,
  isStaycationExperience: boolean,
  isAlwaysIncludedWithNight: boolean
): ValidationErrorResult => {
  const stockConfiguration =
    formValues?.openingConfiguration?.stockConfiguration;
  const priceConfiguration =
    formValues?.openingConfiguration?.priceConfiguration;
  const allowedPaxTypes = formValues?.allowedPaxTypes;

  if (!stockConfiguration || !priceConfiguration) {
    return { isValid: true, errors: [] };
  }

  if (
    stockConfiguration.type === OPENING_CONFIGURATION_TYPE.CONSTANT &&
    priceConfiguration.type === OPENING_CONFIGURATION_TYPE.CONSTANT
  ) {
    const stockForDay = getStockConfigurationForDay(
      stockConfiguration,
      DAY_OF_WEEK.MONDAY
    );
    const priceForDay = getPriceConfigurationForDay(
      priceConfiguration,
      DAY_OF_WEEK.MONDAY
    );

    let error: string | null = null;

    if (isStaycationExperience) {
      error = validateStaycationExperience(
        stockConfiguration,
        priceConfiguration,
        stockForDay,
        priceForDay
      );
    } else if (isAlwaysIncludedWithNight) {
      error = validateAlwaysIncludedWithNightConfiguration(
        priceConfiguration,
        priceForDay,
        allowedPaxTypes
      );
    } else {
      error = validatePriceDifferentiationMode(
        priceForDay,
        priceConfiguration.priceDifferentiationMode,
        allowedPaxTypes
      );
    }

    if (error) {
      return {
        isValid: false,
        errors: [{ message: error, type: priceConfiguration.type }],
      };
    }

    return { isValid: true, errors: [] };
  }

  if (priceConfiguration.type === OPENING_CONFIGURATION_TYPE.DAILY) {
    const daysToValidate = Object.keys(
      priceConfiguration.days || {}
    ) as DayOfWeek[];

    type ErrorData = {
      message: string;
      type: string;
      days: DayOfWeek[];
      fieldsByDay?: Record<DayOfWeek, string[]>;
    };

    const errorsByMessage: Record<string, ErrorData> = {};

    for (const day of daysToValidate) {
      const stockForDay = getStockConfigurationForDay(stockConfiguration, day);
      const priceForDay = getPriceConfigurationForDay(priceConfiguration, day);

      if (stockForDay.isClosed) {
        continue;
      } else if (isAlwaysIncludedWithNight) {
        const error = validateAlwaysIncludedWithNightConfiguration(
          priceConfiguration,
          priceForDay,
          allowedPaxTypes
        );

        if (error) {
          if (!errorsByMessage[error]) {
            errorsByMessage[error] = {
              message: error,
              type: priceConfiguration.type,
              days: [],
            };
          }

          errorsByMessage[error].days.push(day);

          const missingFields = getMissingAlwaysIncludedWithNightFields(
            priceConfiguration,
            priceForDay,
            allowedPaxTypes
          );

          addMissingFieldsToError(errorsByMessage[error], day, missingFields);
        }
      } else if (stockForDay.isClosed) {
        const error = validateClosedOpeningConfiguration(
          priceConfiguration,
          stockForDay,
          priceForDay,
          isAlwaysIncludedWithNight
        );

        if (error) {
          if (!errorsByMessage[error]) {
            errorsByMessage[error] = {
              message: error,
              type: priceConfiguration.type,
              days: [],
            };
          }

          errorsByMessage[error].days.push(day);
        }
      } else {
        const error = validatePriceDifferentiationMode(
          priceForDay,
          priceConfiguration.priceDifferentiationMode,
          allowedPaxTypes
        );

        if (error) {
          if (!errorsByMessage[error]) {
            errorsByMessage[error] = {
              message: error,
              type: priceConfiguration.type,
              days: [],
            };
          }

          errorsByMessage[error].days.push(day);

          const missingFields = getMissingPriceFields(
            priceForDay,
            priceConfiguration.priceDifferentiationMode,
            isAlwaysIncludedWithNight,
            allowedPaxTypes
          );

          addMissingFieldsToError(errorsByMessage[error], day, missingFields);
        }
      }
    }

    if (Object.keys(errorsByMessage).length > 0) {
      const { message, type, days, fieldsByDay } =
        Object.values(errorsByMessage)[0];

      return {
        isValid: false,
        errors: [
          {
            message,
            type,
            days,
            ...(fieldsByDay && { fieldsByDay }),
          },
        ],
      };
    }
  }

  return { isValid: true, errors: [] };
};

export const validateAllConfigurations = (
  formValues: ExperienceInitialFormType
): FormValidationErrors => {
  const isAlwaysIncludedWithNight =
    formValues?.isAlwaysIncludedWithNight ?? false;
  const isStaycationExperience =
    formValues.experienceType === ExperienceType.STAYCATION;

  const stockError = validateStock(formValues);
  const priceError = validatePrice(formValues);
  const configError = validateExperienceConfiguration(
    formValues,
    isStaycationExperience,
    isAlwaysIncludedWithNight
  );

  return {
    stockError,
    priceError: configError.isValid ? priceError : configError,
  };
};
